// Shared presentation pieces for the leaderboards + victory moment
// (PoolLeaderboard, LiveLeaderboard, VictoryModal). No data logic lives here.
// Styles: src/styles/app/leaderboard.css (prefix lb-).
import React, { useEffect, useRef, useState } from 'react';
import OctaAvatar from './OctaAvatar';
import '../styles/app/leaderboard.css';


const AVATAR_BGS = [
  'linear-gradient(135deg, #3d7bff 0%, #1c3a8f 100%)',
  'linear-gradient(135deg, #7d5cff 0%, #3b2a8f 100%)',
  'linear-gradient(135deg, #2bd47a 0%, #13703e 100%)',
  'linear-gradient(135deg, #3fc9d8 0%, #146a75 100%)',
  'linear-gradient(135deg, #ff8a3d 0%, #a3461a 100%)',
  'linear-gradient(135deg, #8a96a8 0%, #3a4354 100%)',
];
export const avatarBg = (userId, isMe) => (isMe
  ? 'linear-gradient(135deg, var(--blood-hot) 0%, var(--blood-deep) 100%)'
  : AVATAR_BGS[(userId || 0) % AVATAR_BGS.length]);

// Remembers a row's last rank (rows are keyed by user, so they persist across
// live refreshes) and reports how far it moved. Resets when the data source changes.
export function useRankMove(rank, scope) {
  const prev = useRef({ rank, scope });
  const [move, setMove] = useState({ delta: 0, tick: 0 });
  useEffect(() => {
    const p = prev.current;
    if (p.scope !== scope || scope == null) {
      setMove((m) => (m.delta ? { delta: 0, tick: 0 } : m));
    } else if (p.rank !== rank) {
      setMove((m) => ({ delta: p.rank - rank, tick: m.tick + 1 }));
    }
    prev.current = { rank, scope };
  }, [rank, scope]);
  return move;
}

// `plain`: nobody has scored yet (everyone tied at 0) — skip medals/tints.
// `scope`: pass only for auto-refreshing (live) lists; rank arrows show when a
// refresh within the same scope reorders the rows. null = no movement tracking.
export function LbRow({ rank, rankLabel, scope = null, index, isMe, plain = false, children }) {
  const { delta, tick } = useRankMove(rank, scope);
  const tone = plain ? '' : rank === 1 ? ' is-gold' : rank === 2 ? ' is-silver' : rank === 3 ? ' is-bronze' : '';
  const moved = delta ? ` moved-${delta > 0 ? 'up' : 'down'}${tick % 2 ? '' : '-b'}` : '';
  return (
    <div className={`lb-row pnp-rise${tone}${isMe ? ' is-me' : ''}${moved}`} style={{ '--i': Math.min(index, 14) }}>
      <div className="lb-rank">
        {rank <= 3 && !plain ? (
          <span className={`lb-medal is-${rank}${rankLabel ? ' is-wide' : ''}`}>{rankLabel || rank}</span>
        ) : (
          <span className="lb-rank-num">{rankLabel || rank}</span>
        )}
        {delta !== 0 && (
          <span key={tick} className={`lb-move ${delta > 0 ? 'is-up' : 'is-down'}`} aria-label={`${delta > 0 ? 'Up' : 'Down'} ${Math.abs(delta)}`}>
            <svg viewBox="0 0 10 10" aria-hidden="true"><path d={delta > 0 ? 'M5 1.5 9 8H1z' : 'M5 8.5 1 2h8z'} fill="currentColor" /></svg>
            {Math.abs(delta)}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

export function LbPodium({ entries, getPts, userId }) {
  const order = [[entries[1], 2], [entries[0], 1], [entries[2], 3]];
  return (
    <div className="lb-podium">
      {order.map(([entry, place]) => entry && (
        <div
          key={entry.user_id}
          className={`lb-spot is-${place}${entry.user_id === userId ? ' is-me' : ''} pnp-rise`}
          style={{ '--i': place === 1 ? 0 : place }}
        >
          {place === 1 && <CrownIcon className="lb-spot-crown" />}
          <div className="lb-spot-avatar">
            <OctaAvatar name={entry.username} size={place === 1 ? 64 : 50} bg={avatarBg(entry.user_id, entry.user_id === userId)} />
            <span className="lb-spot-medal">{entry.rank || place}</span>
          </div>
          <div className="lb-spot-name">{entry.username}</div>
          <div className="lb-spot-pts"><b>{getPts(entry)}</b><span>PTS</span></div>
          <div className="lb-spot-block" aria-hidden="true"><span>{place}</span></div>
        </div>
      ))}
    </div>
  );
}

export function ChevronIcon({ left = false, className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points={left ? '15 18 9 12 15 6' : '9 18 15 12 9 6'} />
    </svg>
  );
}

export function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function TrophyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4z" />
      <path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3" />
    </svg>
  );
}

export function CrownIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 30 22" aria-hidden="true">
      <path d="M2 6.5 8.5 12 15 2l6.5 10L28 6.5 25.5 19h-21z" fill="currentColor" />
      <rect x="4.5" y="19.5" width="21" height="2.5" rx="1.2" fill="currentColor" opacity="0.75" />
    </svg>
  );
}

export function RefreshIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <polyline points="21 3 21 9 15 9" />
    </svg>
  );
}

export function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="15.5" rx="3" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </svg>
  );
}

export function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}
